'use client'

import { useEffect, useRef, useCallback } from 'react'
import { usePathname } from 'next/navigation'
import { v4 as uuid } from 'uuid'
import { getVisitorId } from '@/hooks/use-visitor'
import {
  trackPageViewExtended,
  trackScrollMilestone,
  trackAnalyticsSessionStart,
  trackAnalyticsSessionEnd,
} from '@/lib/analytics'
import { computeEngagementScore } from '@/lib/page-analytics'

const SESSION_TOKEN_KEY = 'iperform_analytics_session'
const LAST_ACTIVITY_KEY = 'iperform_last_activity'
const SESSION_TIMEOUT_MS = 30 * 60 * 1000 // 30 minutes

function getSessionToken(): string {
  if (typeof sessionStorage === 'undefined') return uuid()

  // Check if session is expired
  const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY)
  const existing = sessionStorage.getItem(SESSION_TOKEN_KEY)

  if (existing && lastActivity) {
    const elapsed = Date.now() - parseInt(lastActivity, 10)
    if (elapsed < SESSION_TIMEOUT_MS) {
      return existing
    }
  }

  // Generate new session token
  const newToken = uuid()
  sessionStorage.setItem(SESSION_TOKEN_KEY, newToken)
  return newToken
}

function updateLastActivity() {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString())
  }
}

// Exclude admin and dashboard pages from tracking
function shouldTrack(pathname: string): boolean {
  if (pathname.startsWith('/admin')) return false
  if (/^\/[^/]+\/dashboard/.test(pathname)) return false
  if (pathname.startsWith('/api')) return false
  if (pathname.startsWith('/_next')) return false
  return true
}

/**
 * Page tracking hook — captures page views, scroll depth, and time-on-page.
 * Data sent via fetch (initial) and sendBeacon (enrichment on exit).
 */
export function usePageTracking() {
  const pathname = usePathname()
  const pageViewIdRef = useRef<string | null>(null)
  const startTimeRef = useRef<number>(0)
  const scrollDepthRef = useRef<number>(0)
  const milestonesRef = useRef({ 25: false, 50: false, 75: false, 100: false })
  const sessionTokenRef = useRef<string>('')
  const prevPathnameRef = useRef<string | null>(null)
  const isTrackingRef = useRef(false)

  // Send enrichment beacon for the current page
  const flushCurrentPage = useCallback(() => {
    if (!pageViewIdRef.current || !isTrackingRef.current) return

    const timeOnPageMs = Date.now() - startTimeRef.current
    const scrollDepthMax = scrollDepthRef.current
    const milestones = milestonesRef.current
    const engagementScore = computeEngagementScore(scrollDepthMax, timeOnPageMs, milestones[50])

    const data = JSON.stringify({
      type: 'enrichment',
      visitorId: getVisitorId(),
      sessionToken: sessionTokenRef.current,
      pageViewId: pageViewIdRef.current,
      scrollDepthMax,
      scrollMilestone25: milestones[25],
      scrollMilestone50: milestones[50],
      scrollMilestone75: milestones[75],
      scrollMilestone100: milestones[100],
      timeOnPageMs,
      engagementScore,
    })

    // Try sendBeacon first (reliable on page unload), fall back to fetch
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      navigator.sendBeacon('/api/analytics/pageview', data)
    } else {
      fetch('/api/analytics/pageview', {
        method: 'POST',
        body: data,
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
      }).catch(() => {}) // Silently fail
    }

    // GA4 mirroring
    trackPageViewExtended(
      prevPathnameRef.current || pathname,
      scrollDepthMax,
      timeOnPageMs,
      engagementScore
    )

    const sessionDurationMs = Date.now() - startTimeRef.current
    trackAnalyticsSessionEnd(sessionTokenRef.current, sessionDurationMs, 1)

    pageViewIdRef.current = null
    isTrackingRef.current = false
  }, [pathname])

  // Scroll tracking
  useEffect(() => {
    if (!shouldTrack(pathname)) return

    function handleScroll() {
      requestAnimationFrame(() => {
        const scrollTop = window.scrollY || document.documentElement.scrollTop
        const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight
        if (scrollHeight <= 0) return

        const depth = Math.round((scrollTop / scrollHeight) * 100)
        if (depth > scrollDepthRef.current) {
          scrollDepthRef.current = depth
        }

        // Check milestones
        const thresholds = [25, 50, 75, 100] as const
        for (const t of thresholds) {
          if (depth >= t && !milestonesRef.current[t]) {
            milestonesRef.current[t] = true
            trackScrollMilestone(pathname, t)
          }
        }
      })
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [pathname])

  // Track page view on pathname change
  useEffect(() => {
    if (!shouldTrack(pathname)) return

    // Flush previous page data
    if (prevPathnameRef.current && prevPathnameRef.current !== pathname) {
      flushCurrentPage()
    }

    prevPathnameRef.current = pathname

    // Reset state for new page
    scrollDepthRef.current = 0
    milestonesRef.current = { 25: false, 50: false, 75: false, 100: false }
    startTimeRef.current = Date.now()
    isTrackingRef.current = true

    // Get or create session
    const sessionToken = getSessionToken()
    sessionTokenRef.current = sessionToken
    updateLastActivity()

    // Determine if this is a new session
    const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY)
    const isNewSession = !lastActivity || Date.now() - parseInt(lastActivity, 10) > SESSION_TIMEOUT_MS

    if (isNewSession) {
      trackAnalyticsSessionStart(sessionToken, true)
    }

    // Send initial page view
    const visitorId = getVisitorId()
    fetch('/api/analytics/pageview', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'initial',
        visitorId,
        sessionToken,
        path: pathname,
        title: typeof document !== 'undefined' ? document.title : undefined,
        referrer: typeof document !== 'undefined' ? document.referrer : undefined,
        screenWidth: typeof window !== 'undefined' ? window.innerWidth : undefined,
      }),
    })
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (data?.pageViewId) {
          pageViewIdRef.current = data.pageViewId
        }
      })
      .catch(() => {}) // Silently fail
  }, [pathname, flushCurrentPage])

  // Flush on page unload
  useEffect(() => {
    function handlePageHide() {
      flushCurrentPage()
    }

    window.addEventListener('pagehide', handlePageHide)
    return () => window.removeEventListener('pagehide', handlePageHide)
  }, [flushCurrentPage])
}
